import { useEffect, useState, useCallback, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Leaf, 
  Sparkles, 
  Heart, 
  ShieldCheck, 
  ShoppingBag, 
  Menu, 
  X, 
  ChevronRight, 
  Star,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  ImagePlus,
  Package,
  Droplets,
  Fish,
  Pill,
  Baby,
  Upload,
  Trash2,
  ShoppingCart,
  Plus,
  Minus,
  ZoomIn,
  ZoomOut,
  Maximize2
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image Storage Context
const ImageContext = createContext();

// Default placeholder images for products
// Using local images where available, Unsplash CDN for placeholders
const DEFAULT_IMAGES = {
  // Hair Care System - Using real product images
  hero_hair_bundle: "/images/hero_hair_bundle.jpg",
  hair_bundle_set: "/images/hair_bundle_set.jpg",
  hair_shampoo: "/images/hair_shampoo.jpg",
  hair_shampoo_back: "/images/hair_shampoo_back.jpg",
  // Hair Conditioner - real images
  hair_conditioner: "/images/hair_conditioner.jpg",
  hair_conditioner_back: "/images/hair_conditioner_back.jpg",
  // Hair Mask - real images
  hair_mask: "/images/hair_mask.jpg",
  hair_mask_back: "/images/hair_mask_back.jpg",
  // Hair Serum - real images
  hair_serum: "/images/hair_serum.jpg",
  hair_serum_back: "/images/hair_serum_back.jpg",
  // Collagen - real images
  collagen_front: "/images/collagen_front.jpg",
  collagen_back: "/images/collagen_back.jpg",
  collagen_side: "/images/collagen_side.jpg",
  // Kids Vitamins - real images
  kids_omega: "/images/kids_omega.jpg",
  kids_omega_back: "/images/kids_omega_back.jpg",
  kids_multivitamin: "/images/kids_multivitamin.jpg",
  kids_multivitamin_back: "/images/kids_multivitamin_back.jpg",
  kids_magnesium: "/images/kids_magnesium.jpg",
  kids_magnesium_back: "/images/kids_magnesium_back.jpg",
  kids_d3_k2: "/images/kids_d3_k2.jpg",
  kids_d3_k2_back: "/images/kids_d3_k2_back.jpg",
  // Results section - real before/after images
  before_hair_result: "/images/before_hair_result.jpg",
  after_hair_result: "/images/after_hair_result.jpg"
};

const ImageProvider = ({ children }) => {
  const [images, setImages] = useState(() => {
    const saved = localStorage.getItem('glowvital_images');
    return saved ? JSON.parse(saved) : {};
  });

  const saveImage = useCallback((slotId, imageData) => {
    setImages(prev => {
      const updated = { ...prev, [slotId]: imageData };
      localStorage.setItem('glowvital_images', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const removeImage = useCallback((slotId) => {
    setImages(prev => {
      const updated = { ...prev };
      delete updated[slotId];
      localStorage.setItem('glowvital_images', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const getImage = useCallback((slotId) => {
    // Return uploaded image if exists, otherwise return default placeholder
    return images[slotId] || DEFAULT_IMAGES[slotId] || null;
  }, [images]);

  return (
    <ImageContext.Provider value={{ images, saveImage, removeImage, getImage, DEFAULT_IMAGES }}>
      {children}
    </ImageContext.Provider>
  );
};

const useImages = () => useContext(ImageContext);

// Lightbox Context for Image Zoom
const LightboxContext = createContext();

const LightboxProvider = ({ children }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  const openLightbox = useCallback((imageSrc, altText = 'Product Image') => {
    setLightboxImage(imageSrc);
    setLightboxAlt(altText);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
    setLightboxAlt('');
    document.body.style.overflow = '';
  }, []);

  return (
    <LightboxContext.Provider value={{ lightboxImage, lightboxAlt, openLightbox, closeLightbox }}>
      {children}
    </LightboxContext.Provider>
  );
};

const useLightbox = () => useContext(LightboxContext);

// Lightbox Component with Zoom
const ImageLightbox = () => {
  const { lightboxImage, lightboxAlt, closeLightbox } = useLightbox();
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Reset zoom when image changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [lightboxImage]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, closeLightbox]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (scale > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ 
        x: e.touches[0].clientX - position.x, 
        y: e.touches[0].clientY - position.y 
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && scale > 1 && e.touches.length === 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === containerRef.current) {
      closeLightbox();
    }
  };

  if (!lightboxImage) return null;

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="image-lightbox"
    >
      {/* Close Button */}
      <button
        onClick={closeLightbox}
        className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        data-testid="lightbox-close-btn"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Zoom Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
        <button
          onClick={handleZoomOut}
          disabled={scale <= 1}
          className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30"
          data-testid="lightbox-zoom-out"
        >
          <ZoomOut className="w-5 h-5 text-white" />
        </button>
        <span className="text-white text-sm font-medium min-w-[60px] text-center">
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={handleZoomIn}
          disabled={scale >= 4}
          className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30"
          data-testid="lightbox-zoom-in"
        >
          <ZoomIn className="w-5 h-5 text-white" />
        </button>
        {scale > 1 && (
          <button
            onClick={handleReset}
            className="p-2 hover:bg-white/20 rounded-full transition-colors ml-2"
            data-testid="lightbox-reset"
          >
            <Maximize2 className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Image */}
      <div
        className="max-w-[90vw] max-h-[85vh] overflow-visible"
        style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={(e) => {
          e.stopPropagation();
          if (scale === 1) handleZoomIn();
        }}
      >
        <img
          src={lightboxImage}
          alt={lightboxAlt}
          className="max-w-full max-h-[85vh] object-contain select-none"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            imageRendering: 'high-quality',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            willChange: 'transform'
          }}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      <div className="absolute top-4 left-4 text-white/60 text-xs hidden md:block">
        <p>Click image or + to zoom in</p>
        <p>Drag to pan when zoomed</p>
        <p>Press ESC to close</p>
      </div>
    </div>
  );
};

// Cart Context - Shopping Cart State Management
const CartContext = createContext();

// Product catalog for cart (prices in GBP)
const PRODUCT_CATALOG = {
  "hair-bundle": {
    id: "hair-bundle",
    name: "Hair Growth System",
    description: "Shampoo, Conditioner, Hair Mask + FREE Serum",
    price: 39.99,
    originalPrice: 69.99
  },
  "collagen": {
    id: "collagen",
    name: "Multi Collagen Peptides",
    description: "Types I, II, III, IV & V",
    price: 29.99
  },
  "kids-omega": {
    id: "kids-omega",
    name: "Kid Omega 3-6-9 Gummies",
    description: "Essential fatty acids for brain development",
    price: 29.99
  },
  "kids-multivitamin": {
    id: "kids-multivitamin",
    name: "Kid Multivitamin Gummies",
    description: "Complete daily nutrition",
    price: 29.99
  },
  "kids-magnesium": {
    id: "kids-magnesium",
    name: "Kid Magnesium Gummies",
    description: "Supports relaxation and sleep",
    price: 29.99
  },
  "kids-vitamin-d3k2": {
    id: "kids-vitamin-d3k2",
    name: "Kid Vitamin D3 + K2 Gummies",
    description: "Strong bones and immune health",
    price: 29.99
  }
};

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('glowvital_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('glowvital_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((productId) => {
    const product = PRODUCT_CATALOG[productId];
    if (!product) return;

    setCart(prev => {
      const existingItem = prev.find(item => item.id === productId);
      if (existingItem) {
        return prev.map(item =>
          item.id === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    
    toast.success(`${product.name} added to cart!`, { duration: 2000 });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      cartTotal,
      cartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

const BENEFITS = [
  { icon: Leaf, title: "Hair", description: "Nourish and strengthen your hair from root to tip" },
  { icon: Sparkles, title: "Skin", description: "Radiant, youthful skin through natural ingredients" },
  { icon: Heart, title: "Wellness", description: "Holistic approach to your overall wellbeing" },
  { icon: ShieldCheck, title: "Daily Support", description: "Essential nutrients for everyday vitality" }
];

const TESTIMONIALS = [
  {
    name: "Sarah M.",
    rating: 5,
    text: "My hair feels stronger after just a few weeks."
  },
  {
    name: "Emma T.",
    rating: 5,
    text: "The collagen tastes great and mixes easily."
  },
  {
    name: "Laura K.",
    rating: 5,
    text: "My kids love the gummies!"
  }
];

const FAQ_ITEMS = [
  {
    question: "How long until I see results?",
    answer: "Most customers start noticing improvements within 4-6 weeks of consistent use. Hair growth and skin improvements vary by individual, but our products are formulated for optimal absorption and effectiveness."
  },
  {
    question: "Are the products suitable for all hair types?",
    answer: "Yes! Our Hair Growth System is formulated to work with all hair types - straight, wavy, curly, or coily. The gentle yet effective rosemary and mint formula nourishes without weighing down your hair."
  },
  {
    question: "What makes your collagen different?",
    answer: "Our Multi Collagen Peptides contains 5 types of collagen from multiple sources, providing comprehensive support for skin, hair, nails, and joints. It's also unflavoured and easily dissolves in any beverage."
  },
  {
    question: "Are the kids vitamins safe for daily use?",
    answer: "Yes, our kids vitamins are specially formulated for daily use. They contain age-appropriate doses of essential vitamins and minerals, with no artificial colours or flavours. Always follow the recommended dosage on the label."
  }
];

// Collagen Product Data
const COLLAGEN_PRODUCT = {
  id: "collagen",
  name: "Multi Collagen Peptides Types I, II, III, IV & V",
  category: "Supplements",
  description: "Premium collagen peptide blend designed to support skin, hair, nails and joints.",
  price: 29.99,
  benefits: [
    "5 types of collagen for complete support",
    "Supports skin elasticity & hydration",
    "Strengthens hair & nails",
    "Promotes joint health & mobility",
    "Unflavoured & easy to mix"
  ],
  images: [
    { id: "collagen_front", label: "Front View" },
    { id: "collagen_back", label: "Back View" },
    { id: "collagen_side", label: "Side View" }
  ]
};

// Hair Care System Bundle Data
const HAIR_CARE_SYSTEM = {
  id: "hair-growth-system",
  name: "Hair Growth System",
  description: "A complete rosemary and mint hair care routine designed to support stronger, healthier looking hair.",
  originalPrice: 69.99,
  bundlePrice: 39.99,
  bundleProducts: [
    { name: "Hair Shampoo", id: "hair_shampoo", backId: "hair_shampoo_back" },
    { name: "Hair Conditioner", id: "hair_conditioner", backId: "hair_conditioner_back" },
    { name: "Hair Mask", id: "hair_mask", backId: "hair_mask_back" }
  ],
  freeGift: {
    name: "Hair Care Serum",
    id: "hair_serum",
    backId: "hair_serum_back",
    description: "Leave-in serum for growth and protection"
  },
  heroImage: { id: "hero_hair_bundle", label: "Hair Growth System" },
  bundleImage: { id: "hair_bundle_set", label: "Hair Bundle" }
};

// Kids Vitamins Collection Data
const KIDS_VITAMINS = {
  collectionName: "Kids Vitamins for Growth & Development",
  description: "Daily nutritional support for kids' immunity, brain development and strong bones.",
  price: 29.99,
  products: [
    {
      id: "kids-omega",
      name: "Kid Omega 3-6-9 Gummies",
      description: "Essential fatty acids for brain development and cognitive function.",
      image: { id: "kids_omega", label: "Product Front" },
      backImage: { id: "kids_omega_back", label: "Supplement Facts" },
      benefits: ["Supports brain development", "Helps focus and learning", "Supports heart health"]
    },
    {
      id: "kids-multivitamin",
      name: "Kid Multivitamin Gummies",
      description: "Complete daily nutrition with essential vitamins and minerals.",
      image: { id: "kids_multivitamin", label: "Product Front" },
      backImage: { id: "kids_multivitamin_back", label: "Supplement Facts" },
      benefits: ["Supports immune system", "Daily vitamin support", "Helps maintain energy"]
    },
    {
      id: "kids-magnesium",
      name: "Kid Magnesium Gummies",
      description: "Supports relaxation, sleep quality and muscle function.",
      image: { id: "kids_magnesium", label: "Product Front" },
      backImage: { id: "kids_magnesium_back", label: "Supplement Facts" },
      benefits: ["Supports relaxation", "Helps better sleep", "Muscle support"]
    },
    {
      id: "kids-vitamin-d3k2",
      name: "Kid Vitamin D3 + K2 Gummies",
      description: "Essential for strong bones, teeth and immune health.",
      image: { id: "kids_d3_k2", label: "Product Front" },
      backImage: { id: "kids_d3_k2_back", label: "Supplement Facts" },
      benefits: ["Supports strong bones", "Helps calcium absorption", "Supports immune health"]
    }
  ]
};

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-[#E7E5E4]" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-['Playfair_Display'] text-2xl font-medium text-[#292524]" data-testid="logo">
            GlowVital
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#hair-system" className="text-[#57534E] hover:text-[#4A6741] transition-colors" data-testid="nav-hair">Hair Care</a>
            <a href="#collagen" className="text-[#57534E] hover:text-[#4A6741] transition-colors" data-testid="nav-collagen">Collagen</a>
            <a href="#kids-vitamins" className="text-[#57534E] hover:text-[#4A6741] transition-colors" data-testid="nav-kids">Kids Vitamins</a>
            <a href="#reviews" className="text-[#57534E] hover:text-[#4A6741] transition-colors" data-testid="nav-reviews">Reviews</a>
            <a href="#faq" className="text-[#57534E] hover:text-[#4A6741] transition-colors" data-testid="nav-faq">FAQ</a>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {/* Cart Button */}
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 text-[#57534E] hover:text-[#4A6741] transition-colors"
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#4A6741] text-white text-xs rounded-full flex items-center justify-center font-medium" data-testid="cart-count">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>
            
            <a href="#hair-system" className="btn-primary text-sm px-6 py-3" data-testid="nav-shop-btn">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Shop Now
            </a>
          </div>

          {/* Mobile: Cart + Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Cart Button */}
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 text-[#292524]"
              data-testid="mobile-cart-btn"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#4A6741] text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              className="p-2 text-[#292524]" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E7E5E4]" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <a href="#hair-system" className="text-[#57534E] hover:text-[#4A6741] py-2" onClick={() => setMobileMenuOpen(false)}>Hair Care</a>
              <a href="#collagen" className="text-[#57534E] hover:text-[#4A6741] py-2" onClick={() => setMobileMenuOpen(false)}>Collagen</a>
              <a href="#kids-vitamins" className="text-[#57534E] hover:text-[#4A6741] py-2" onClick={() => setMobileMenuOpen(false)}>Kids Vitamins</a>
              <a href="#reviews" className="text-[#57534E] hover:text-[#4A6741] py-2" onClick={() => setMobileMenuOpen(false)}>Reviews</a>
              <a href="#faq" className="text-[#57534E] hover:text-[#4A6741] py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <a href="#hair-system" className="btn-primary text-center py-3" onClick={() => setMobileMenuOpen(false)}>Shop Now</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Cart Drawer Component
const CartDrawer = ({ onCheckout, isLoading }) => {
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart();
  const formatPrice = (p) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(p);

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 z-[60] transition-opacity"
        onClick={() => setIsCartOpen(false)}
        data-testid="cart-backdrop"
      />
      
      {/* Drawer */}
      <div 
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl flex flex-col cart-drawer-enter"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E7E5E4]">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 text-[#4A6741]" />
            <h2 className="font-['Playfair_Display'] text-xl font-medium text-[#292524]">
              Your Cart
            </h2>
            {cartCount > 0 && (
              <span className="text-sm text-[#57534E]">({cartCount} items)</span>
            )}
          </div>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-[#F5F5F4] rounded-full transition-colors"
            data-testid="close-cart-btn"
          >
            <X className="w-5 h-5 text-[#57534E]" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 rounded-full bg-[#F5F5F4] flex items-center justify-center mb-4">
                <ShoppingBag className="w-10 h-10 text-[#A8A29E]" />
              </div>
              <h3 className="font-medium text-[#292524] mb-2">Your cart is empty</h3>
              <p className="text-sm text-[#57534E] mb-6">Add some products to get started</p>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="btn-primary text-sm px-6 py-3"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div 
                  key={item.id}
                  className="flex gap-4 p-4 bg-[#FAFAF9] rounded-xl"
                  data-testid={`cart-item-${item.id}`}
                >
                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-[#292524] text-sm mb-1 truncate">
                      {item.name}
                    </h4>
                    <p className="text-xs text-[#57534E] mb-2 truncate">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#292524]">
                        {formatPrice(item.price)}
                      </span>
                      {item.originalPrice && (
                        <span className="text-xs text-[#78716C] line-through">
                          {formatPrice(item.originalPrice)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-1 text-[#78716C] hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    
                    <div className="flex items-center gap-2 bg-white rounded-full border border-[#E7E5E4] px-2 py-1">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 text-[#57534E] hover:text-[#4A6741] transition-colors"
                        data-testid={`decrease-qty-${item.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-medium text-[#292524] w-6 text-center" data-testid={`qty-${item.id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 text-[#57534E] hover:text-[#4A6741] transition-colors"
                        data-testid={`increase-qty-${item.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <span className="text-sm font-medium text-[#292524]">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {cart.length > 0 && (
          <div className="border-t border-[#E7E5E4] p-6 bg-white">
            {/* Subtotal */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-[#57534E]">Subtotal</span>
              <span className="text-xl font-semibold text-[#292524]" data-testid="cart-total">
                {formatPrice(cartTotal)}
              </span>
            </div>
            
            <p className="text-xs text-[#78716C] mb-4">
              Shipping calculated at checkout
            </p>

            {/* Checkout Button */}
            <button
              onClick={onCheckout}
              disabled={isLoading}
              className="btn-primary w-full py-4 disabled:opacity-50"
              data-testid="proceed-to-checkout-btn"
            >
              {isLoading ? (
                <span className="spinner" />
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5 mr-2" />
                  Proceed to Checkout
                </>
              )}
            </button>

            {/* Continue Shopping */}
            <button
              onClick={() => setIsCartOpen(false)}
              className="w-full mt-3 py-3 text-sm font-medium text-[#57534E] hover:text-[#4A6741] transition-colors"
              data-testid="continue-shopping-link"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// Image Display Component (read-only, with lightbox zoom)
const ImageUpload = ({ slotId, label, className = "", size = "default" }) => {
  const { getImage } = useImages();
  const { openLightbox } = useLightbox();
  const currentImage = getImage(slotId);

  const sizeClasses = {
    default: "min-h-[200px]",
    small: "min-h-[120px]",
    large: "min-h-[300px] md:min-h-[400px]"
  };

  const handleClick = () => {
    if (currentImage) {
      openLightbox(currentImage, label);
    }
  };

  if (currentImage) {
    return (
      <div 
        className={`relative rounded-xl overflow-hidden bg-[#F5F5F4] p-2.5 flex items-center justify-center cursor-zoom-in group ${className}`}
        data-testid={`image-display-${slotId}`}
        onClick={handleClick}
      >
        <img 
          src={currentImage} 
          alt={label}
          loading="lazy"
          decoding="async"
          style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
          className="rounded-lg transition-transform duration-200 group-hover:scale-[1.02]"
        />
        {/* Zoom hint on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="bg-white/90 rounded-full p-2 shadow-lg">
            <ZoomIn className="w-5 h-5 text-[#4A6741]" />
          </div>
        </div>
      </div>
    );
  }

  // Fallback placeholder for missing images
  return (
    <div 
      className={`relative rounded-xl overflow-hidden bg-[#F5F5F4] ${sizeClasses[size]} flex items-center justify-center ${className}`}
      data-testid={`image-placeholder-${slotId}`}
    >
      <div className="text-center p-4">
        <ImagePlus className="w-8 h-8 text-[#A8A29E] mx-auto mb-2" />
        <p className="text-sm text-[#78716C]">{label}</p>
      </div>
    </div>
  );
};

// Image Gallery with Upload Support
const ImageGalleryUpload = ({ images, productName }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const { getImage } = useImages();

  return (
    <div className="space-y-4" data-testid={`gallery-${productName.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Main Image */}
      <ImageUpload 
        slotId={images[selectedImage].id} 
        label={images[selectedImage].label}
        className="aspect-square w-full"
        size="default"
      />
      
      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((image, index) => {
            const hasImage = getImage(image.id);
            return (
              <button
                key={image.id}
                onClick={() => setSelectedImage(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors bg-[#F5F5F4] p-1 ${
                  selectedImage === index ? 'border-[#4A6741]' : 'border-transparent hover:border-[#D6E0D4]'
                }`}
                data-testid={`thumb-${image.id}`}
              >
                {hasImage ? (
                  <img src={hasImage} alt={image.label} loading="lazy" decoding="async" className="w-full h-full object-contain" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImagePlus className="w-4 h-4 text-[#A8A29E]" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Product Dual Image Component (Front + Back)
const ProductDualImage = ({ frontId, backId, frontLabel = "Product Front", backLabel = "Ingredients" }) => {
  const [showBack, setShowBack] = useState(false);
  const { getImage } = useImages();
  const hasFrontImage = getImage(frontId);
  const hasBackImage = getImage(backId);

  return (
    <div className="space-y-2" data-testid={`dual-image-${frontId}`}>
      {/* Main Image */}
      <ImageUpload 
        slotId={showBack ? backId : frontId}
        label={showBack ? backLabel : frontLabel}
        className="aspect-square w-full"
        size="small"
      />
      
      {/* Thumbnail Toggle */}
      <div className="flex gap-2 justify-center">
        <button
          onClick={() => setShowBack(false)}
          className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-colors bg-[#F5F5F4] p-0.5 ${
            !showBack ? 'border-[#4A6741]' : 'border-transparent hover:border-[#D6E0D4]'
          }`}
          title="Product Front"
        >
          {hasFrontImage ? (
            <img src={hasFrontImage} alt={frontLabel} loading="lazy" decoding="async" className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImagePlus className="w-3 h-3 text-[#A8A29E]" />
            </div>
          )}
        </button>
        <button
          onClick={() => setShowBack(true)}
          className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-colors bg-[#F5F5F4] p-0.5 ${
            showBack ? 'border-[#4A6741]' : 'border-transparent hover:border-[#D6E0D4]'
          }`}
          title="Ingredients / Back"
        >
          {hasBackImage ? (
            <img src={hasBackImage} alt={backLabel} loading="lazy" decoding="async" className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[8px] text-[#A8A29E]">
              Back
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

// Price Display Component
const PriceDisplay = ({ price, originalPrice = null }) => {
  const formatPrice = (p) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(p);
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-2xl font-semibold text-[#292524]">{formatPrice(price)}</span>
      {originalPrice && (
        <span className="text-lg text-[#78716C] line-through">{formatPrice(originalPrice)}</span>
      )}
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center hero-gradient pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
              <Leaf className="w-4 h-4 mr-2" />
              Premium Wellness
            </span>
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] text-[#292524] mb-6">
              GlowVital Store
            </h1>
            <p className="text-lg md:text-xl text-[#57534E] leading-relaxed font-light mb-8 max-w-lg">
              Premium wellness essentials for hair, skin and family health.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#hair-system" className="btn-primary" data-testid="hero-shop-btn">
                Shop Hair Growth System
                <ChevronRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
          
          <div className="relative animate-fade-in-up delay-200">
            <div className="relative rounded-2xl overflow-hidden soft-shadow">
              <ImageUpload 
                slotId="hero_hair_bundle"
                label="Hair Growth System Bundle"
                className="w-full aspect-[4/5]"
                size="large"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 md:bottom-8 md:-left-8 glass-effect rounded-xl p-4 soft-shadow border border-[#E7E5E4]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#4A6741] flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#292524]">Trusted by</p>
                  <p className="text-lg font-semibold text-[#4A6741]">10,000+ customers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Hair Growth System Section
const HairGrowthSystemSection = ({ onBuyNow, loadingProduct }) => {
  const formatPrice = (price) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(price);
  const { addToCart } = useCart();

  return (
    <section id="hair-system" className="section-padding bg-[#F5F5F4]" data-testid="hair-system-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            <Droplets className="w-4 h-4 mr-2" />
            Main Offer
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#292524] mb-4">
            {HAIR_CARE_SYSTEM.name}
          </h2>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            {HAIR_CARE_SYSTEM.description}
          </p>
        </div>

        {/* Bundle Card */}
        <div className="bg-white rounded-3xl p-6 md:p-10 soft-shadow max-w-5xl mx-auto mb-12" data-testid="hair-bundle-card">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Product Image */}
            <div>
              <ImageUpload 
                slotId="hair_bundle_set"
                label="Hair Growth System Bundle"
                className="aspect-square w-full"
                size="default"
              />
            </div>

            {/* Bundle Info */}
            <div>
              {/* Limited Offer Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-[#D97706] text-white mb-6">
                LIMITED OFFER
              </div>

              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium text-[#292524] mb-6">
                Complete Hair Care Bundle
              </h3>

              {/* Products Included */}
              <div className="mb-6">
                <p className="text-sm font-medium text-[#57534E] mb-3">Products included:</p>
                <ul className="space-y-2">
                  {HAIR_CARE_SYSTEM.bundleProducts.map((product, idx) => (
                    <li key={idx} className="flex items-center text-[#292524]">
                      <ChevronRight className="w-4 h-4 text-[#4A6741] mr-2" />
                      {product.name}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Free Gift */}
              <div className="bg-[#E8EFEC] rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-[#4A6741]" />
                  <span className="font-semibold text-[#4A6741]">FREE BONUS</span>
                </div>
                <p className="text-[#292524] font-medium">{HAIR_CARE_SYSTEM.freeGift.name}</p>
                <p className="text-sm text-[#57534E]">{HAIR_CARE_SYSTEM.freeGift.description}</p>
              </div>

              {/* Pricing */}
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl text-[#78716C] line-through">{formatPrice(HAIR_CARE_SYSTEM.originalPrice)}</span>
                  <span className="text-4xl font-bold text-[#292524]">{formatPrice(HAIR_CARE_SYSTEM.bundlePrice)}</span>
                </div>
                <p className="text-[#4A6741] font-semibold">Save £30 today</p>
              </div>

              {/* Free Gift Message */}
              <p className="text-sm text-[#57534E] mb-6 italic">
                Buy the Hair Growth System today and receive a FREE Hair Care Serum.
              </p>

              {/* Purchase Buttons */}
              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => addToCart('hair-bundle')}
                  className="w-full py-3 px-6 text-sm font-medium text-[#4A6741] border-2 border-[#4A6741] bg-transparent hover:bg-[#E8EFEC] rounded-full transition-colors"
                  data-testid="add-to-cart-hair-bundle-btn"
                >
                  <ShoppingCart className="w-4 h-4 inline mr-2" />
                  Add to Cart
                </button>
                <button 
                  onClick={() => onBuyNow('hair-bundle')}
                  disabled={loadingProduct === 'hair-bundle'}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="buy-now-hair-bundle-btn"
                >
                  {loadingProduct === 'hair-bundle' ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Buy Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Individual Hair Products Grid */}
        <div className="mt-12">
          <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] text-center mb-8">
            Products in Bundle
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {/* Hair Shampoo */}
            <div className="bg-white rounded-xl p-4 soft-shadow">
              <ProductDualImage 
                frontId="hair_shampoo"
                backId="hair_shampoo_back"
                frontLabel="Shampoo Front"
                backLabel="Ingredients"
              />
              <p className="text-sm font-medium text-[#292524] text-center mt-2">Hair Shampoo</p>
            </div>
            
            {/* Hair Conditioner */}
            <div className="bg-white rounded-xl p-4 soft-shadow">
              <ProductDualImage 
                frontId="hair_conditioner"
                backId="hair_conditioner_back"
                frontLabel="Conditioner Front"
                backLabel="Ingredients"
              />
              <p className="text-sm font-medium text-[#292524] text-center mt-2">Hair Conditioner</p>
            </div>
            
            {/* Hair Mask */}
            <div className="bg-white rounded-xl p-4 soft-shadow">
              <ProductDualImage 
                frontId="hair_mask"
                backId="hair_mask_back"
                frontLabel="Mask Front"
                backLabel="Ingredients"
              />
              <p className="text-sm font-medium text-[#292524] text-center mt-2">Hair Mask</p>
            </div>
            
            {/* Hair Serum (Free Gift) */}
            <div className="bg-white rounded-xl p-4 soft-shadow relative">
              <div className="absolute -top-2 -right-2 bg-[#4A6741] text-white text-[10px] px-2 py-1 rounded-full font-medium z-10">
                FREE
              </div>
              <ProductDualImage 
                frontId="hair_serum"
                backId="hair_serum_back"
                frontLabel="Serum Front"
                backLabel="Ingredients"
              />
              <p className="text-sm font-medium text-[#292524] text-center mt-2">Hair Care Serum</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Collagen Section
const CollagenSection = ({ onBuyNow, loadingProduct }) => {
  const formatPrice = (price) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(price);
  const { addToCart } = useCart();

  return (
    <section id="collagen" className="section-padding bg-[#FAFAF9]" data-testid="collagen-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            <Pill className="w-4 h-4 mr-2" />
            Supplements
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#292524] mb-4">
            Collagen
          </h2>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            Premium collagen peptides for complete beauty and wellness support
          </p>
        </div>

        {/* Main Product Card */}
        <div className="bg-white rounded-3xl p-6 md:p-10 soft-shadow max-w-5xl mx-auto" data-testid="collagen-product-card">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Image Gallery */}
            <div>
              <ImageGalleryUpload images={COLLAGEN_PRODUCT.images} productName={COLLAGEN_PRODUCT.name} />
            </div>

            {/* Product Details */}
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#E8EFEC] text-[#4A6741] mb-4">
                {COLLAGEN_PRODUCT.category}
              </span>
              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium text-[#292524] mb-4">
                {COLLAGEN_PRODUCT.name}
              </h3>
              <p className="text-[#57534E] mb-6 leading-relaxed">
                {COLLAGEN_PRODUCT.description}
              </p>

              <ul className="space-y-3 mb-8">
                {COLLAGEN_PRODUCT.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start text-[#57534E]">
                    <ChevronRight className="w-5 h-5 text-[#4A6741] mr-2 flex-shrink-0 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <PriceDisplay price={COLLAGEN_PRODUCT.price} />
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button 
                    onClick={() => addToCart('collagen')}
                    className="py-3 px-6 text-sm font-medium text-[#4A6741] border-2 border-[#4A6741] bg-transparent hover:bg-[#E8EFEC] rounded-full transition-colors"
                    data-testid="add-to-cart-collagen-btn"
                  >
                    <ShoppingCart className="w-4 h-4 inline mr-2" />
                    Add to Cart
                  </button>
                  <button 
                    onClick={() => onBuyNow('collagen')}
                    disabled={loadingProduct === 'collagen'}
                    className="btn-primary disabled:opacity-50"
                    data-testid="buy-now-collagen-btn"
                  >
                    {loadingProduct === 'collagen' ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Buy Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Kids Vitamins Section
const KidsVitaminsSection = ({ onBuyNow, loadingProduct }) => {
  const { addToCart } = useCart();
  
  return (
    <section id="kids-vitamins" className="section-padding bg-[#F5F5F4]" data-testid="kids-vitamins-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            <Baby className="w-4 h-4 mr-2" />
            Kids Collection
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#292524] mb-4">
            {KIDS_VITAMINS.collectionName}
          </h2>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            {KIDS_VITAMINS.description}
          </p>
        </div>

        {/* Products Grid - 4 columns desktop, 2 columns mobile */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {KIDS_VITAMINS.products.map((product) => (
            <div 
              key={product.id}
              className="product-card"
              data-testid={`kids-product-${product.id}`}
            >
              <div className="p-4">
                <ProductDualImage 
                  frontId={product.image.id}
                  backId={product.backImage.id}
                  frontLabel={product.image.label}
                  backLabel={product.backImage.label}
                />
              </div>
              <div className="p-5 pt-0">
                <h3 className="font-['Playfair_Display'] text-base md:text-lg font-medium text-[#292524] mb-3">
                  {product.name}
                </h3>
                
                {/* Benefits List */}
                <ul className="space-y-2 mb-4">
                  {product.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start text-sm text-[#57534E]">
                      <ChevronRight className="w-4 h-4 text-[#4A6741] mr-1.5 flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <p className="text-xl font-bold text-[#292524] text-center mb-4">£29.99</p>

                {/* Purchase Buttons */}
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={() => addToCart(product.id)}
                    className="w-full py-2.5 px-4 text-sm font-medium text-[#4A6741] border-2 border-[#4A6741] bg-transparent hover:bg-[#E8EFEC] rounded-full transition-colors"
                    data-testid={`add-to-cart-${product.id}-btn`}
                  >
                    <ShoppingCart className="w-4 h-4 inline mr-1" />
                    Add to Cart
                  </button>
                  <button 
                    onClick={() => onBuyNow(product.id)}
                    disabled={loadingProduct === product.id}
                    className="w-full py-2.5 px-4 text-sm font-medium text-white bg-[#4A6741] hover:bg-[#3A5A40] rounded-full transition-colors disabled:opacity-50"
                    data-testid={`buy-now-${product.id}-btn`}
                  >
                    {loadingProduct === product.id ? (
                      <span className="spinner inline-block w-4 h-4" />
                    ) : (
                      'Buy Now'
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  return (
    <section id="reviews" className="section-padding bg-[#FAFAF9]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            Customer Reviews
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#292524] mb-4">
            What Our Customers Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {TESTIMONIALS.map((testimonial, index) => (
            <div 
              key={index} 
              className="testimonial-card animate-fade-in-up text-center"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`testimonial-${index}`}
            >
              <div className="flex gap-1 mb-4 stars justify-center">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <p className="text-[#57534E] leading-relaxed mb-4 italic">
                "{testimonial.text}"
              </p>
              <p className="font-medium text-[#292524]">— {testimonial.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className="section-padding bg-[#FAFAF9]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            Support
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#292524] mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {FAQ_ITEMS.map((item, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-white rounded-xl border border-[#E7E5E4] px-6 overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left font-medium text-[#292524] hover:text-[#4A6741] py-5 hover:no-underline">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-[#57534E] pb-5">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Before & After Results Section
const ResultsSection = () => {
  return (
    <section className="section-padding bg-[#FAFAF9]" data-testid="results-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            <Sparkles className="w-4 h-4 mr-2" />
            Real Results
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#292524] mb-4">
            Real Hair Growth Results
          </h2>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            See the transformation after using the GlowVital Hair Growth System.
          </p>
        </div>

        {/* Before & After Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="text-center">
            <div className="bg-white rounded-2xl p-4 soft-shadow mb-3">
              <ImageUpload 
                slotId="before_hair_result"
                label="Before Photo"
                className="aspect-[4/3] w-full"
                size="default"
              />
            </div>
            <p className="text-sm font-medium text-[#57534E]">Before</p>
          </div>
          <div className="text-center">
            <div className="bg-white rounded-2xl p-4 soft-shadow mb-3">
              <ImageUpload 
                slotId="after_hair_result"
                label="After Photo"
                className="aspect-[4/3] w-full"
                size="default"
              />
            </div>
            <p className="text-sm font-medium text-[#57534E]">After</p>
          </div>
        </div>

        {/* Caption */}
        <p className="text-center text-[#78716C] italic">
          Results after consistent use of the Hair Growth System.
        </p>
      </div>
    </section>
  );
};

// Trust Badges Section
const TrustBadgesSection = () => {
  const badges = [
    {
      icon: Leaf,
      title: "Premium Ingredients",
      description: "Carefully selected ingredients for hair health."
    },
    {
      icon: ShieldCheck,
      title: "Quality Tested",
      description: "Each batch is tested for quality."
    },
    {
      icon: Heart,
      title: "Cruelty Free",
      description: "No animal testing."
    },
    {
      icon: Sparkles,
      title: "Made with Care",
      description: "Developed with high quality standards."
    }
  ];

  return (
    <section className="section-padding bg-[#F5F5F4]" data-testid="trust-badges-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#292524] mb-4">
            Why Customers Trust GlowVital
          </h2>
        </div>

        {/* Badges Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {badges.map((badge, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-white rounded-xl soft-shadow"
              data-testid={`trust-badge-${index}`}
            >
              <div className="w-14 h-14 rounded-full bg-[#E8EFEC] flex items-center justify-center mx-auto mb-4">
                <badge.icon className="w-7 h-7 text-[#4A6741]" />
              </div>
              <h3 className="font-medium text-[#292524] mb-2">{badge.title}</h3>
              <p className="text-sm text-[#57534E]">{badge.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      step: "1",
      title: "Cleanse",
      description: "Cleanse your scalp with GlowVital Shampoo to remove buildup and prepare follicles."
    },
    {
      step: "2",
      title: "Nourish",
      description: "Nourish hair with Conditioner and Hair Mask to strengthen strands and support growth."
    },
    {
      step: "3",
      title: "Stimulate",
      description: "Apply Hair Serum daily to stimulate follicles and improve thickness."
    }
  ];

  return (
    <section className="section-padding bg-[#FAFAF9]" data-testid="how-it-works-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#292524] mb-4">
            How the GlowVital Hair System Works
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-6 soft-shadow text-center">
              <div className="w-12 h-12 rounded-full bg-[#4A6741] flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">{item.step}</span>
              </div>
              <h3 className="font-medium text-[#292524] text-lg mb-3">{item.title}</h3>
              <p className="text-sm text-[#57534E] leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Key Ingredients Section
const KeyIngredientsSection = () => {
  const ingredients = [
    { icon: Leaf, name: "Rosemary Extract", benefit: "supports hair growth" },
    { icon: Sparkles, name: "Biotin", benefit: "strengthens hair structure" },
    { icon: Droplets, name: "Argan Oil", benefit: "deeply nourishes hair" },
    { icon: Heart, name: "Vitamin Complex", benefit: "improves scalp health" }
  ];

  return (
    <section className="section-padding bg-[#F5F5F4]" data-testid="key-ingredients-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#292524] mb-4">
            Powered by Natural Active Ingredients
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {ingredients.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-5 soft-shadow text-center">
              <div className="w-12 h-12 rounded-full bg-[#E8EFEC] flex items-center justify-center mx-auto mb-3">
                <item.icon className="w-6 h-6 text-[#4A6741]" />
              </div>
              <h3 className="font-medium text-[#292524] mb-1">{item.name}</h3>
              <p className="text-xs text-[#57534E]">{item.benefit}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Customer Results Section
const CustomerResultsSection = () => {
  const testimonials = [
    { text: "My hair feels thicker after just a few weeks.", name: "Emma", location: "London" },
    { text: "The shampoo and serum combo really works.", name: "Daniel", location: "Manchester" },
    { text: "I finally found a system that helps my hair grow.", name: "Sarah", location: "Birmingham" },
    { text: "My hair looks healthier and stronger after a month.", name: "Daniel", location: "Manchester" },
    { text: "I noticed less hair fall and my scalp feels much better.", name: "Sophie", location: "Birmingham" },
    { text: "The rosemary formula smells amazing and my hair feels thicker.", name: "James", location: "Leeds" },
    { text: "After a few weeks my hair looks fuller and easier to style.", name: "Laura", location: "Bristol" },
    { text: "I love the natural ingredients. My hair feels stronger already.", name: "Alex", location: "Liverpool" }
  ];

  return (
    <section className="section-padding bg-[#FAFAF9]" data-testid="customer-results-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#292524] mb-4">
            Real Customer Results
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-6 soft-shadow text-center">
              <div className="flex gap-1 justify-center mb-4 stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <p className="text-[#57534E] italic mb-4">"{item.text}"</p>
              <p className="text-sm font-medium text-[#292524]">— {item.name}, {item.location}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  return (
    <section className="section-padding bg-[#E8EFEC]" data-testid="social-proof-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        {/* Main Badge */}
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#4A6741] mb-6">
          <Heart className="w-12 h-12 text-white" />
        </div>
        
        <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#292524] mb-4">
          Trusted by Thousands of Customers
        </h2>
        
        {/* Large Number */}
        <div className="bg-white rounded-2xl py-8 px-12 inline-block soft-shadow mb-6">
          <p className="text-5xl md:text-6xl font-bold text-[#4A6741]">10,000+</p>
          <p className="text-lg text-[#57534E] mt-2">Happy Customers</p>
        </div>
        
        {/* Supporting Text */}
        <p className="text-lg text-[#57534E] max-w-xl mx-auto">
          Join thousands of customers who trust GlowVital for healthier hair.
        </p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="section-padding bg-[#F5F5F4]" data-testid="contact-section">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#D6E0D4] text-[#4A6741] mb-6">
            <Mail className="w-4 h-4 mr-2" />
            Get in Touch
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium tracking-tight text-[#292524] mb-4">
            Contact Us
          </h2>
          <p className="text-[#57534E]">
            Have a question? We'd love to hear from you.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 md:p-10 soft-shadow">
          <form 
            action="https://formsubmit.co/support@glowvitalstore.co.uk" 
            method="POST"
            className="space-y-6"
            data-testid="contact-form"
          >
            <input type="hidden" name="_subject" value="New message from GlowVitalStore" />
            <input type="hidden" name="_captcha" value="false" />

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-[#292524] mb-2">
                Name
              </label>
              <input 
                type="text" 
                name="name" 
                id="name"
                required
                className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-[#FAFAF9] text-[#292524] placeholder-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-transparent transition-all"
                placeholder="Your name"
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#292524] mb-2">
                Email
              </label>
              <input 
                type="email" 
                name="email" 
                id="email"
                required
                className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-[#FAFAF9] text-[#292524] placeholder-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-transparent transition-all"
                placeholder="your@email.com"
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-[#292524] mb-2">
                Message
              </label>
              <textarea 
                name="message" 
                id="message"
                required
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-[#FAFAF9] text-[#292524] placeholder-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-transparent transition-all resize-none"
                placeholder="How can we help you?"
                data-testid="contact-message-input"
              />
            </div>

            <button 
              type="submit"
              className="btn-primary w-full py-4"
              data-testid="contact-submit-btn"
            >
              <Mail className="w-5 h-5 mr-2" />
              Send Message
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="privacy-policy-page">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-[#E7E5E4]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="font-['Playfair_Display'] text-2xl font-medium text-[#292524]">
              GlowVital
            </Link>
            <Link to="/" className="text-[#57534E] hover:text-[#4A6741] transition-colors text-sm">
              ← Back to Store
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#292524] mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-stone max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Introduction
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                GlowVital respects your privacy and is committed to protecting your personal data. This policy explains how we collect, use, and protect your information when you visit our website.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Information We Collect
              </h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                We may collect the following data:
              </p>
              <ul className="list-disc list-inside text-[#57534E] space-y-2">
                <li>Name</li>
                <li>Email address</li>
                <li>Shipping address</li>
                <li>Payment information (processed securely via Stripe)</li>
                <li>Website usage data through analytics tools</li>
              </ul>
            </section>

            {/* How We Use Your Data */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                How We Use Your Data
              </h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                Your data is used to:
              </p>
              <ul className="list-disc list-inside text-[#57534E] space-y-2">
                <li>Process orders</li>
                <li>Deliver products</li>
                <li>Provide customer support</li>
                <li>Improve website functionality</li>
                <li>Prevent fraud</li>
              </ul>
            </section>

            {/* Third Party Services */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Third Party Services
              </h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                We use trusted third-party services including:
              </p>
              <ul className="list-disc list-inside text-[#57534E] space-y-2">
                <li>Stripe for secure payments</li>
                <li>Google Analytics for website traffic analysis</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Data Security
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                We take appropriate measures to protect your data and prevent unauthorized access.
              </p>
            </section>

            {/* User Rights (GDPR) */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                User Rights (GDPR)
              </h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                Users have the right to:
              </p>
              <ul className="list-disc list-inside text-[#57534E] space-y-2">
                <li>Request access to personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of personal data</li>
                <li>Withdraw consent for data processing</li>
              </ul>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Contact Information
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                If you have any questions about this Privacy Policy you may contact us via the{' '}
                <Link to="/#contact" className="text-[#4A6741] hover:underline">
                  website contact page
                </Link>.
              </p>
            </section>
          </div>

          {/* Back to Store */}
          <div className="mt-12 pt-8 border-t border-[#E7E5E4]">
            <Link 
              to="/" 
              className="inline-flex items-center text-[#4A6741] hover:text-[#3A5A40] transition-colors font-medium"
            >
              ← Back to GlowVital Store
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms & Conditions Page
const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="terms-conditions-page">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-[#E7E5E4]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="font-['Playfair_Display'] text-2xl font-medium text-[#292524]">
              GlowVital
            </Link>
            <Link to="/" className="text-[#57534E] hover:text-[#4A6741] transition-colors text-sm">
              ← Back to Store
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#292524] mb-8">
            Terms & Conditions
          </h1>
          
          <div className="prose prose-stone max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Introduction
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                Welcome to GlowVital. By accessing our website and placing orders, you agree to be bound by these Terms & Conditions. Please read them carefully before using our services.
              </p>
            </section>

            {/* Products & Orders */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Products & Orders
              </h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                All products are subject to availability. We reserve the right to limit quantities and refuse orders at our discretion. Prices are displayed in GBP and include VAT where applicable.
              </p>
            </section>

            {/* Payment */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Payment
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                Payment is processed securely through Stripe. We accept major credit and debit cards. Your payment information is encrypted and never stored on our servers.
              </p>
            </section>

            {/* Shipping & Delivery */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Shipping & Delivery
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                We aim to dispatch orders within 1-3 business days. Delivery times may vary depending on your location. Shipping costs and estimated delivery times are displayed at checkout.
              </p>
            </section>

            {/* Returns & Refunds */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Returns & Refunds
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                If you are not satisfied with your purchase, you may return unopened products within 30 days for a full refund. Please contact our customer support team to initiate a return.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Intellectual Property
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                All content on this website, including text, images, logos, and designs, is the property of GlowVital and is protected by copyright laws. Unauthorized use is prohibited.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Limitation of Liability
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                GlowVital shall not be liable for any indirect, incidental, or consequential damages arising from the use of our products or services.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Changes to Terms
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                We reserve the right to update these Terms & Conditions at any time. Changes will be posted on this page with an updated revision date.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#292524] mb-4">
                Contact
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                If you have any questions about these Terms & Conditions, please contact us via the{' '}
                <Link to="/#contact" className="text-[#4A6741] hover:underline">
                  website contact page
                </Link>.
              </p>
            </section>
          </div>

          {/* Back to Store */}
          <div className="mt-12 pt-8 border-t border-[#E7E5E4]">
            <Link 
              to="/" 
              className="inline-flex items-center text-[#4A6741] hover:text-[#3A5A40] transition-colors font-medium"
            >
              ← Back to GlowVital Store
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#292524] text-[#FAFAF9]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          
          {/* Column 1: Brand */}
          <div>
            <h3 className="font-['Playfair_Display'] text-xl font-medium mb-4">GlowVital Store</h3>
            <p className="text-[#A8A29E] text-sm leading-relaxed mb-5">
              Premium wellness essentials for hair, skin and overall health.
            </p>
            <div className="space-y-2 text-sm text-[#A8A29E]">
              <a href="mailto:support@glowvitalstore.co.uk" className="flex items-center gap-2 hover:text-[#FAFAF9] transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4 flex-shrink-0" />
                support@glowvitalstore.co.uk
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>London, United Kingdom</span>
              </div>
            </div>
          </div>

          {/* Column 2: Customer Support */}
          <div>
            <h4 className="font-medium text-base mb-4">Customer Support</h4>
            <ul className="space-y-3">
              <li>
                <a href="#contact" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-contact">
                  Contact Us
                </a>
              </li>
              <li>
                <a href="#faq" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-faq">
                  FAQ
                </a>
              </li>
              <li>
                <a href="#" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-shipping-info">
                  Shipping Information
                </a>
              </li>
              <li>
                <a href="#" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-returns">
                  Returns & Refunds
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div>
            <h4 className="font-medium text-base mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy-policy" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-and-conditions" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-terms">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <a href="#" className="text-[#A8A29E] hover:text-[#FAFAF9] transition-colors text-sm" data-testid="footer-link-shipping">
                  Shipping & Returns
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Trust */}
          <div>
            <h4 className="font-medium text-base mb-4">Trust</h4>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2 text-sm text-[#A8A29E]">
                <ShieldCheck className="w-4 h-4 text-[#4A6741] flex-shrink-0" />
                Secure Checkout
              </li>
              <li className="flex items-center gap-2 text-sm text-[#A8A29E]">
                <Package className="w-4 h-4 text-[#4A6741] flex-shrink-0" />
                Free UK Shipping
              </li>
              <li className="flex items-center gap-2 text-sm text-[#A8A29E]">
                <Heart className="w-4 h-4 text-[#4A6741] flex-shrink-0" />
                30-Day Money Back Guarantee
              </li>
            </ul>
            
            {/* Payment Icons */}
            <div className="flex flex-wrap gap-2">
              {/* Visa */}
              <div className="w-11 h-7 bg-white rounded flex items-center justify-center" title="Visa">
                <svg className="w-9 h-5" viewBox="0 0 50 16" fill="none">
                  <path d="M19.5 1.5L17 14.5H14L16.5 1.5H19.5Z" fill="#1A1F71"/>
                  <path d="M28 1.5L24 10.5L23.5 8L22 2.5C22 2.5 21.8 1.5 20.5 1.5H15.5L15.4 1.8C15.4 1.8 17 2.2 18.8 3.3L22 14.5H25.5L32 1.5H28Z" fill="#1A1F71"/>
                  <path d="M35 1.5C34 1.5 33.5 2 33.2 2.8L28 14.5H31.5L32.2 12.5H36.5L37 14.5H40L37.5 1.5H35ZM33.2 10L35 4.5L36 10H33.2Z" fill="#1A1F71"/>
                  <path d="M12 1.5L8.5 10.5L8 8L6.5 2.5C6.5 2.5 6.3 1.5 5 1.5H0L0 1.8C0 1.8 2 2.3 4 3.8C5.8 5.2 6.5 6.5 6.5 6.5L9.5 14.5H13L17 1.5H12Z" fill="#1A1F71"/>
                </svg>
              </div>
              {/* Mastercard */}
              <div className="w-11 h-7 bg-white rounded flex items-center justify-center" title="Mastercard">
                <svg className="w-7 h-5" viewBox="0 0 32 20">
                  <circle cx="10" cy="10" r="8" fill="#EB001B"/>
                  <circle cx="22" cy="10" r="8" fill="#F79E1B"/>
                  <path d="M16 3.5C17.8 5 19 7.4 19 10C19 12.6 17.8 15 16 16.5C14.2 15 13 12.6 13 10C13 7.4 14.2 5 16 3.5Z" fill="#FF5F00"/>
                </svg>
              </div>
              {/* Amex */}
              <div className="w-11 h-7 bg-[#006FCF] rounded flex items-center justify-center" title="American Express">
                <span className="text-white text-[7px] font-bold">AMEX</span>
              </div>
              {/* Apple Pay */}
              <div className="w-11 h-7 bg-black rounded flex items-center justify-center" title="Apple Pay">
                <span className="text-white text-[7px] font-medium">Pay</span>
              </div>
              {/* Google Pay */}
              <div className="w-11 h-7 bg-white rounded flex items-center justify-center" title="Google Pay">
                <span className="text-[8px] font-medium text-gray-700">G Pay</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#3D3A39] mt-12 pt-8">
          <p className="text-sm text-[#78716C] text-center">
            © 2026 GlowVital Store. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const [loadingProduct, setLoadingProduct] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentChecked, setPaymentChecked] = useState(false);
  const { cart, clearCart, setIsCartOpen } = useCart();

  // Check for payment return
  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you for your purchase.', {
          duration: 5000,
        });
        clearCart(); // Clear cart after successful payment
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  }, [clearCart]);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId && !paymentChecked) {
      setPaymentChecked(true);
      toast.info('Checking payment status...', { duration: 2000 });
      checkPaymentStatus(sessionId);
      
      // Clear URL params after handling
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. Feel free to try again!', { duration: 4000 });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams, paymentChecked, checkPaymentStatus]);

  // Buy Now - Direct checkout for single product
  const handleBuyNow = async (productId) => {
    setLoadingProduct(productId);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/create`, {
        product_id: productId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoadingProduct(null);
    }
  };

  // Cart Checkout - Checkout all items in cart
  const handleCartCheckout = async () => {
    if (cart.length === 0) return;
    
    setCheckoutLoading(true);
    
    try {
      const originUrl = window.location.origin;
      
      // Prepare cart items for checkout
      const cartItems = cart.map(item => ({
        product_id: item.id,
        quantity: item.quantity
      }));
      
      const response = await axios.post(`${API}/checkout/cart`, {
        items: cartItems,
        origin_url: originUrl
      });

      if (response.data.url) {
        setIsCartOpen(false);
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Cart checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navigation />
      <CartDrawer onCheckout={handleCartCheckout} isLoading={checkoutLoading} />
      <HeroSection />
      <HairGrowthSystemSection onBuyNow={handleBuyNow} loadingProduct={loadingProduct} />
      <HowItWorksSection />
      <KeyIngredientsSection />
      <ResultsSection />
      <TrustBadgesSection />
      <CollagenSection onBuyNow={handleBuyNow} loadingProduct={loadingProduct} />
      <KidsVitaminsSection onBuyNow={handleBuyNow} loadingProduct={loadingProduct} />
      <CustomerResultsSection />
      <SocialProofSection />
      <ReviewsSection />
      <FAQSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Cookie Consent Banner
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(() => {
    return localStorage.getItem('glowvital_cookie_consent') !== 'accepted';
  });

  const handleAccept = () => {
    localStorage.setItem('glowvital_cookie_consent', 'accepted');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] bg-[#292524] text-[#FAFAF9] p-4 md:p-6 shadow-lg"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-sm text-[#A8A29E] text-center md:text-left">
          We use cookies to improve your experience and analyze website traffic. By clicking Accept, you agree to our use of cookies.
        </p>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link 
            to="/privacy-policy" 
            className="text-sm text-[#A8A29E] hover:text-[#FAFAF9] underline transition-colors"
            data-testid="cookie-learn-more"
          >
            Learn More
          </Link>
          <button
            onClick={handleAccept}
            className="px-6 py-2 text-sm font-medium bg-[#4A6741] text-white rounded-full hover:bg-[#3A5A40] transition-colors"
            data-testid="cookie-accept-btn"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <ImageProvider>
        <LightboxProvider>
          <div className="App">
            <Toaster position="top-center" richColors />
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
              </Routes>
              <CookieConsentBanner />
              <ImageLightbox />
            </BrowserRouter>
          </div>
        </LightboxProvider>
      </ImageProvider>
    </CartProvider>
  );
}

export default App;
